<template>
    <BaseCard padding="md" class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between md:gap-4">
        <div class="flex-1 min-w-0 flex flex-col gap-1">
            <div class="flex items-center gap-2 flex-wrap">
                <span class="body-semibold text-ink truncate">{{ campaign.name }}</span>
                <StatusChip state="pending">Draft</StatusChip>
            </div>
            <div class="body-small text-ink-muted">{{ audienceLine }}</div>
            <div class="details text-ink-muted">{{ dateLine }}</div>
        </div>

        <div class="flex items-center gap-2 md:shrink-0">
            <BaseButton theme="neutral" size="sm" icon-only :aria-label="`Delete draft ${campaign.name}`" title="Delete draft" :disabled="busy" @click="emit('delete', campaign)">
                <i class="bi bi-trash" aria-hidden="true"></i>
            </BaseButton>
            <BaseButton theme="neutral" size="sm" :aria-label="`Launch ${campaign.name}`" :disabled="busy" @click="emit('launch', campaign)">
                <i class="bi bi-send" aria-hidden="true"></i>
                Launch
            </BaseButton>
            <BaseButton type="button" theme="primary" size="sm" :aria-label="`Edit ${campaign.name}`" :disabled="busy" @click="emit('edit', campaign)">Edit</BaseButton>
        </div>
    </BaseCard>
</template>

<script setup lang="ts">
import type { AdminCampaignSummary } from "~vue/api/generated/reflections-admin";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import StatusChip from "~vue/components/StatusChip.vue";
import { formatDate } from "~vue/utils/threeSixty";
import { computed } from "vue";

interface Props {
    campaign: AdminCampaignSummary;
    busy: boolean;
}

const props = defineProps<Props>();
const emit = defineEmits<{
    (event: "delete", campaign: AdminCampaignSummary): void;
    (event: "launch", campaign: AdminCampaignSummary): void;
    (event: "edit", campaign: AdminCampaignSummary): void;
}>();

// Campaigns always invite the whole eligible org, so the audience is the eligible count.
const audienceLine = computed(() => {
    const count = props.campaign.eligible_participant_count;
    const parts = [props.campaign.template.name];
    if (props.campaign.is_self_assessment_only) {
        parts.push("Self-assessment only");
    }
    parts.push(`Everyone with assessments enabled · ${count} ${count === 1 ? "person" : "people"}`);
    return parts.join(" · ");
});

// The campaign records who created it but not who last edited it, so only
// the creator is attributed.
const dateLine = computed(() => {
    const created = formatDate(props.campaign.created_at, "short");
    const creator = props.campaign.created_by_name ? ` by ${props.campaign.created_by_name}` : "";
    const edited = formatDate(props.campaign.updated_at, "short");
    const parts = [created ? `Created ${created}${creator}` : "", edited ? `Last edited ${edited}` : ""].filter(Boolean);
    return parts.join(" · ");
});
</script>
